/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import i18n from '@remind/i18n';
import LinkedStateMixin from 'react-addons-linked-state-mixin';
import React from 'react';
import { keys } from 'lodash';
import invariant from 'invariant';

import AlertDialog from 'components/AlertDialog.jsx';
import Button from 'components/Button.jsx';
import ButtonGroup from 'components/ButtonGroup.jsx';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import Composer from 'chat/components/Composer.jsx';
import ComposerFilePreview from 'chat/components/ComposerFilePreview.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import DateConstants from 'constants/DateConstants';
import DropZoneModal from 'components/DropZoneModal.jsx';
import FileUploaderMixin from 'components/mixins/FileUploaderMixin';
import File from 'entities/File';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import focusNode from 'utils/focusNode';
import Glyph from 'components/Glyph.jsx';
import Image from 'components/Image.jsx';
import KeyConstants from 'constants/KeyConstants';
import LanguageHoverCard from 'chat/components/LanguageHoverCard.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import MessageComposerActions from 'chat/actions/MessageComposerActions';
import MessageComposerStore from 'chat/stores/MessageComposerStore';
import ResponsiveMixin from 'components/mixins/ResponsiveMixin';
import SettingsStore from 'stores/SettingsStore';
import Timing from 'lib/Timing';
import TranslationActions from 'actions/TranslationActions';
import { mountModal } from 'lib/modal';
import ComposerAlert from './ComposerAlert.jsx';

import './styles/MessageComposer.scss';

const OFFICE_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const AFTER_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const CHARACTER_LIMIT = 1500;
const UI_CONTEXT = 'chat_composer';

const MessageComposer = React.createClass({
  propTypes: {
    isNewChat: React.PropTypes.bool,
    chat: React.PropTypes.instanceOf(Chat).isRequired,
    onMessageSend: React.PropTypes.func.isRequired,
    width: React.PropTypes.number,
    openSideBar: React.PropTypes.func,
    hideComposerAlert: React.PropTypes.bool,
  },

  mixins: [
    LinkedStateMixin,
    FileUploaderMixin,
    ResponsiveMixin,
  ],

  statics: {
    MEDIUM: 750,
    SMALL: 650,
    XSMALL: 550,
    XXSMALL: 450,
  },

  getInitialState() {
    return {
      composerError: false,
      latestTranslation: '',
      text: '',
      translationBoxOpened: false,
      uploadedFile: null,
    };
  },

  componentWillMount() {
    if (this.props.chat.id) {
      const state = MessageComposerStore.getComposerStates(this.props.chat.id);
      if (state) {
        this.setState({
          ...state,
        });
      }
    }
  },

  componentWillUpdate(nextProps: Object) {
    const currentChatId = this.props.chat.id;
    const nextChatId = nextProps.chat.id;
    const chatChanged = (nextChatId !== currentChatId);

    if (chatChanged) {
      this.destroyDropzone();

      // When switching chats, we save the composer state for persisted chats to be restored if we come back to this chat.
      if (!this.props.chat.isNew()) {
        const stateBeforeTimeout = this.state;
        // Timeout to place action at end of dispatch stack
        setTimeout(() => {
          MessageComposerActions.saveState(currentChatId, stateBeforeTimeout);
        }, 0);
      }

      if (this.refs.composer) {
        this.refs.composer.clear();
      }

      if (nextChatId) {
        const state = MessageComposerStore.getComposerStates(nextChatId) || this.getInitialState();
        this.setState({ ...state });
      }
    }
  },

  componentDidUpdate(prevProps: Object) {
    if (this.props.width !== prevProps.width) {
      this.hasResized(); // ResponsiveMixin
    }
  },

  componentWillUnmount() {
    if (!this.props.chat.isNew()) {
      setTimeout(() => {
        MessageComposerActions.saveState(this.props.chat.id, this.state);
      }, 0);
    }
    this.destroyDropzone();
  },

  onUploadFailed(file: File) {
    if (file.getSize() > this.getMaxFilesize()) {
      this.resetDropzone();
      mountModal(
        <AlertDialog
          glyph="caution"
          glyphColor="lava"
          title={`File "${file.getName()}" is too large.`}
          subtext="Files must be less than 10MB"
        />,
        this
      );
    }
  },

  getDropzoneId() {
    if (this.props.isNewChat) {
      return 'newChat';
    } else {
      return this.props.chat.id;
    }
  },

  getTranslationBox() {
    const languages = SettingsStore.getLanguages().primary;

    if (!languages) {
      return null;
    }

    const buttons = [];

    let displayedMax = keys(languages).length;
    let displayed = 0;

    if (this.isComponentSize(MessageComposer.MEDIUM)) {
      displayedMax -= 1;
    } else if (this.isComponentSize(MessageComposer.SMALL)) {
      displayedMax -= 2;
    } else if (this.isComponentSize(MessageComposer.XSMALL)) {
      displayedMax -= 3;
    } else if (this.isComponentSize(MessageComposer.XXSMALL)) {
      displayedMax -= 5;
    }

    for (const lang in languages) {
      displayed += 1;
      if (displayed <= displayedMax) {
        buttons.push(
          <Button
            key={`translate-${lang}`}
            eventContextName={UI_CONTEXT}
            eventElementName="translate_button"
            eventProperties={{
              language_code: lang,
            }}
            onClick={() => this.translateTo(lang)}>
            {languages[lang]}
          </Button>
        );
      }
    }

    buttons.push(
      <LayerTrigger
        key="show-more"
        layer={
          <LanguageHoverCard
            eventContextName={UI_CONTEXT}
            onLanguagePicked={lang => this.translateTo(lang)}
          />
        }
        placement="top"
        trigger="click"
        alignment="middle">
        <Button
          eventContextName={UI_CONTEXT}
          eventElementName="show_more_languages_button"
          level="secondary"
          color="thunder"
          size="xsmall">
          <Glyph name="ellipses" solo />
        </Button>
      </LayerTrigger>
    );

    return (
      <div
        className={cx('MessageComposer-translationBox', {
          'MessageComposer-translationBox-opened': this.state.translationBoxOpened,
          'MessageComposer-translationBox-closed': !this.state.translationBoxOpened,
        })}>

        <div className="MessageComposer-translationBox-buttons">
          <ButtonGroup level="secondary" color="thunder" size="xsmall">
            {buttons}
          </ButtonGroup>
        </div>

        <Button
          className="MessageComposer-translationBox-closeButton"
          eventContextName={UI_CONTEXT}
          eventElementName="close_translation_box"
          onClick={this.toggleTranslationBoxOpened}
          level="text">
          <Glyph name="close-circle" color="brand" size="xlarge" solo />
        </Button>
      </div>
    );
  },

  toggleTranslationBoxOpened() {
    this.setState(({ translationBoxOpened }) => ({ translationBoxOpened: !translationBoxOpened }));
  },

  allowSendAfterHours: null,

  allowSendOutsideOfficeHours: {},

  chatState: {},

  handleTextChange(event: SyntheticEvent) {
    // $FlowIssue
    const text = event.target.value;

    if (text === this.state.text) {
      return;
    }

    this.setState({
      text,
      composerError: text.trim().length > CHARACTER_LIMIT,
    });
  },

  confirmSendMessageAfterHours() {
    const timeWarning = Timing.isEarly() ?
        'It’s pretty early.' :
        'It’s pretty late.';
    const question = 'Send this message anyway?';

    const title = (
      <div>
        <Image
          className="MessageComposer-confirmImage"
          src={require('assets/images/after-hours.svg')}
        />
        <p>{timeWarning} {question}</p>
      </div>
    );

    mountModal(
      <ConfirmDialog
        title={title}
        eventContext="ChatMessageComposer"
        size="xsmall"
        onConfirm={() => {
          // $FlowFixMe
          this.allowSendAfterHours = setTimeout(() => {
            this.allowSendAfterHours = null;
          }, AFTER_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  },

  confirmSendMessageOutsideOfOfficeHours() {
    const { chat } = this.props;
    let otherChatMember = null;
    let nextAvailability = null;
    // XXX weird use of some() since the return value isn't used
    chat.getOtherChatMembers().some(member => {
      const officeHours = member.getOfficeHours();
      if (officeHours && !officeHours.isAvailableNow()) {
        otherChatMember = member;
        nextAvailability = officeHours.getNextAvailability();
        return true;
      }
      return false;
    });

    invariant(nextAvailability, 'unable to determine nextAvailability');

    const startTime = nextAvailability.getStartTime();
    const date = new Date();
    const diff = ((nextAvailability.getDay() + 7) - date.getDay()) % 7;
    date.setHours(Math.floor(startTime / 3600));
    date.setMinutes((startTime % 3600) / 60);

    const day = diff === 1 ?
      i18n.getText('tomorrow') :
      DateConstants.WEEKDAYS[nextAvailability.getDay()];
    const time = i18n.getTime(date, { hour: 'numeric', minute: '2-digit' });

    const otherChatMemberName = otherChatMember ? otherChatMember.getName() : '???';

    mountModal(
      <ConfirmDialog
        size="xsmall"
        glyph="clock-bubble"
        glyphColor="violet"
        title={`Uh-oh! ${otherChatMemberName} won’t be back until ${day} at ${time}.`}
        subtext={
          `Office hours for ${otherChatMemberName} won’t start until ${day} at ${time},
          so they may not receive your message right now. Send anyway?`
        }
        onConfirm={() => {
          this.allowSendOutsideOfficeHours[chat.id] = setTimeout(() => {
            delete this.allowSendOutsideOfficeHours[chat.id];
          }, OFFICE_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  },

  destroyDropzone() {
    if (this.props.chat.isNew()) {
      const files = this.getFiles();
      if (files.length > 0) {
        this.removeFile(files[0]);
      }
      this.destroyDropzoneIfEmpty();
    } else {
      this.destroyDropzoneIfEmpty();
    }
  },

  focus() {
    if (this.refs.composer) {
      this.refs.composer.focus();
    }
  },

  handleEnableRepliesButtonClick(event: Event) {
    event.preventDefault();
    ChatActions.enableReplies(this.props.chat);
  },

  isAttachEnabled() {
    return (
      !this.state.translationBoxOpened &&
      (this.isChatEnabled() || this.props.chat.isNew()) &&
      !this.getFiles()[0]
    );
  },

  isAttachWithDragEnabled() {
    return this.isChatEnabled();
  },

  isChatEnabled() {
    return this.props.chat.isPresent() && !this.props.chat.isUnsubscribed();
  },

  isValidMessage() {
    const fileData = this.getFileData(this.getFiles()[0]);
    if (fileData) {
      const fileEntity = this.getFileEntities()[0];
      return (fileData.progress === 100 && fileEntity.hasUrls());
    }
    return this.state.text.trim() !== '';
  },

  getPlaceholder() {
    const chatTitle = this.props.chat.getTitle();
    if (chatTitle) {
      return `Message ${chatTitle}`;
    } else {
      return 'Type a message';
    }
  },

  sendMessage() {
    const { chat } = this.props;

    if (chat.isOutsideOfOtherOfficeHours() && !this.allowSendOutsideOfficeHours[chat.id]) {
      this.confirmSendMessageOutsideOfOfficeHours();
    } else if ((Timing.isLate() || Timing.isEarly()) && !this.allowSendAfterHours) {
      this.confirmSendMessageAfterHours();
    } else {
      this.props.onMessageSend(this.state.text, this.getFileEntities());
      this.refs.composer.clear();
      this.setState({ text: '' });
      this.removeAllFiles();
    }
  },

  // typecheck: ignore statement
  handleReturn(event: SyntheticKeyboardEvent): boolean {
    if (event.keyCode === KeyConstants.ENTER && !event.shiftKey) {
      this.handleSubmit(event);
      return true;
    }
    return false;
  },

  // typecheck: ignore statement
  handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    this.submitComposer();
  },

  submitComposer() {
    if (!this.submitDisabled()) {
      this.sendMessage();
    }
  },

  submitDisabled() {
    return (
      this.state.translationBoxOpened ||
      this.state.composerError ||
      !this.isChatEnabled() ||
      !this.isValidMessage()
    );
  },

  translateTo(lang: string) {
    TranslationActions
      .translate(this.state.text, lang)
      .then(response => {
        this.setState({
          text: response.translation,
          latestTranslation: lang,
          translationBoxOpened: false,
        });
      });
  },

  renderMessageComposer() {
    const {
      composerError,
      // $FlowFixMe
      isDraggingFile,
      // $FlowFixMe
      isDraggingFileInner,
      translationBoxOpened,
      latestTranslation,
    } = this.state;

    if (!this.props.chat.canSend()) {
      return null;
    }

    const { chat } = this.props;

    let errorMessage = null;
    if (composerError) {
      errorMessage = (
        <div className="MessageComposer-error">
          Sorry, you can’t send more than {i18n.getNumber(CHARACTER_LIMIT)} characters
        </div>
      );
    }

    const file = this.getFiles()[0];
    let fileData = this.getFileData(file);

    let dropZoneModal;
    if (isDraggingFile && this.isAttachWithDragEnabled()) {
      dropZoneModal = (
        <DropZoneModal
          isHovering={isDraggingFileInner}
          isFileUploaded={!!file}
        />
      );
    }

    const attachButton = (
      <div className="MessageComposer-attach" ref="dropzone" key={chat.id}>
        <div ref="dropzone_attach">
          <Button
            className={`addFileButton_${this.getDropzoneId()}`}
            onClick={() => focusNode(this.refs.composer)}
            disabled={!this.isAttachEnabled()}
            level="text">
            <Glyph name="paperclip" className="MessageComposer-actionGlyph" />
            Attach
          </Button>
        </div>
        {dropZoneModal}
      </div>
    );

    const translateButton = (
      <Button
        className="MessageComposer-translate"
        disabled={this.state.text.length < 1}
        eventContextName={UI_CONTEXT}
        eventElementName={
          translationBoxOpened ?
            'hide_translation_box' :
            'show_translation_box'
        }
        onClick={() =>
          this.setState({ translationBoxOpened: !translationBoxOpened })
        }
        level="text">
        <Glyph name="globe" className="MessageComposer-actionGlyph" />
        Translate
      </Button>
    );

    if (isDraggingFile && !fileData) {
      // use a fake file when hovering so that the composer is the correct size
      fileData = {};
    }
    let filePreview = null;
    if (fileData || isDraggingFile) {
      filePreview = (
        <div className="MessageComposer-preview">
          <ComposerFilePreview
            className={cx({
              draggingFile: isDraggingFile,
            })}
            progress={fileData.progress}
            thumbnail={fileData.thumbnail}
            type={fileData.type}
            error={fileData.error}
            onDelete={() => this.removeFile(file)}
            onRetry={() => this.retryUpload(file)}
            file={this.state.uploadedFile}
          />
        </div>
      );
    }

    const eventProperties = {};

    if (latestTranslation) {
      eventProperties.language_code = latestTranslation;
    }

    const otherMembersPlaceholderText = this.getPlaceholder();

    return (
      <div className="MessageComposer-composerContainer">
        <FlexibleBlock flex="left">
          <div>
            <Composer
              ref="composer"
              className="MessageComposer-composer"
              onChange={this.handleTextChange}
              onReturnKeyPress={this.handleReturn}
              error={composerError}
              onUpdate={this.checkLimit}
              value={this.state.text}
              placeholder={otherMembersPlaceholderText}
              disabled={!this.isChatEnabled()}>
              {filePreview}
              {this.getTranslationBox()}
            </Composer>
            {errorMessage}
            <div className="MessageComposer-actions">
              {attachButton}
              {translateButton}
            </div>
          </div>
          <Button
            className="MessageComposer-sendButton"
            eventContextName={UI_CONTEXT}
            eventElementName="send_button"
            eventProperties={eventProperties}
            level="primary"
            size="small"
            onClick={this.handleSubmit}
            disabled={this.submitDisabled()}>
            Send
          </Button>
        </FlexibleBlock>
      </div>
    );
  },

  render() {
    const composerAlert = (
      <ComposerAlert
        chat={this.props.chat}
        openSideBar={this.props.openSideBar}
      />
    );
    return (
      <div
        className={cx('MessageComposer', this.props.className)}
        style={{ width: this.props.width }}>
        {!this.props.hideComposerAlert && composerAlert}
        {this.renderMessageComposer()}
      </div>
    );
  },
});

export default MessageComposer;



// WEBPACK FOOTER //
// ./src/chat/components/MessageComposer.jsx